import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Gauge, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const colourMap = {
  green: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', pill: 'bg-emerald-500 text-white', bar: 'bg-emerald-500' },
  blue: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', pill: 'bg-blue-500 text-white', bar: 'bg-blue-500' },
  yellow: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', pill: 'bg-amber-500 text-white', bar: 'bg-amber-500' },
  red: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', pill: 'bg-red-500 text-white', bar: 'bg-red-500' },
};

const SubjectCell = ({ row, testId }) => {
  const c = colourMap[row.colour] || colourMap.yellow;
  return (
    <div className={`rounded-lg border p-3 ${c.bg}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] uppercase tracking-wide text-gray-500">{row.subject}</p>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.pill}`}>{row.label}</span>
      </div>
      <div className="flex items-end justify-between">
        <p className={`text-2xl font-bold ${c.text}`}>{row.readiness_score}%</p>
        <p className="text-[11px] text-gray-500 text-right">
          {row.students_covered} student{row.students_covered !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
        <div className={`h-full ${c.bar}`} style={{ width: `${Math.min(100, row.readiness_score)}%` }} />
      </div>
      {row.weakest_student?.name && (
        <p className="text-[10px] text-gray-500 mt-2 truncate" title={`Weakest: ${row.weakest_student.name} @ ${row.weakest_student.score}%`}>
          Focus: <span className="font-medium text-gray-700">{row.weakest_student.name}</span> {row.weakest_student.score != null ? `(${row.weakest_student.score}%)` : ''}
        </p>
      )}
    </div>
  );
};

const ClassReadinessRollup = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/head-teacher/class-readiness`);
        setData(res.data);
      } catch (e) {
        console.error('class-readiness load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Group rows by (class, section)
  const grouped = useMemo(() => {
    const rows = data?.rows || [];
    const map = {};
    for (const r of rows) {
      const key = `${r.class_level}-${r.section}`;
      if (!map[key]) map[key] = { class_level: r.class_level, section: r.section, subjects: [] };
      map[key].subjects.push(r);
    }
    // Sort cohorts by lowest cohort readiness (weakest first)
    return Object.values(map).map((coh) => {
      const scores = coh.subjects.map((s) => s.readiness_score);
      const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      return { ...coh, cohort_readiness: Math.round(avg * 10) / 10 };
    }).sort((a, b) => a.cohort_readiness - b.cohort_readiness);
  }, [data]);

  const summary = data?.summary || {};

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 flex items-center justify-center" data-testid="class-readiness-loading">
        <Loader2 className="animate-spin text-purple-500" size={22} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="class-readiness-rollup">
      <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gauge size={22} />
          <div>
            <h3 className="font-semibold text-lg">Class Exam Readiness</h3>
            <p className="text-slate-300 text-xs">Per-class × subject readiness rolled up from student gauges (last 90 days)</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-white" data-testid="class-readiness-overall">{summary.avg_readiness || 0}%</p>
          <p className="text-xs uppercase tracking-wide text-slate-300">{summary.avg_readiness_label || '—'}</p>
        </div>
      </div>
      <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap gap-2 text-[11px]">
        <span className="flex items-center gap-1 text-gray-600"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Ready {summary.ready || 0}</span>
        <span className="flex items-center gap-1 text-gray-600"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> On Track {summary.on_track || 0}</span>
        <span className="flex items-center gap-1 text-gray-600"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Needs Prep {summary.needs_prep || 0}</span>
        <span className="flex items-center gap-1 text-gray-600"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Not Ready {summary.not_ready || 0}</span>
        <span className="ml-auto text-gray-400">{summary.cohorts || 0} cohorts</span>
      </div>

      <div className="p-4 space-y-3">
        {grouped.length === 0 ? (
          <div className="text-center py-10">
            <AlertCircle className="mx-auto text-gray-300 mb-3" size={40} />
            <p className="text-gray-500 text-sm">No topic-test data yet. Once students start attempting Topic Tests, this rollup fills up automatically.</p>
          </div>
        ) : (
          grouped.map((coh) => {
            const key = `${coh.class_level}-${coh.section}`;
            const cohColour = coh.cohort_readiness >= 75 ? 'green' : coh.cohort_readiness >= 60 ? 'blue' : coh.cohort_readiness >= 40 ? 'yellow' : 'red';
            const c = colourMap[cohColour];
            const open = expanded[key] !== false; // default open
            return (
              <div key={key} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`cohort-${key}`}>
                <button
                  onClick={() => setExpanded({ ...expanded, [key]: !open })}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${c.pill}`}>
                      Class {coh.class_level} · {coh.section}
                    </span>
                    <span className="text-sm text-gray-600">{coh.subjects.length} subject{coh.subjects.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className={`text-xl font-bold ${c.text}`}>{coh.cohort_readiness}%</p>
                  </div>
                </button>
                {open && (
                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 bg-white">
                    {coh.subjects.map((row, i) => (
                      <SubjectCell key={`${row.subject}-${i}`} row={row} testId={`cohort-${key}-${row.subject.toLowerCase().replace(/\s+/g, '-')}`} />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ClassReadinessRollup;
